import { supabase } from './supabase';
import { extractFnError } from './odooInvoices';
import type { MonthlyStatRaw } from './monthlyBilling';

/**
 * Services Supabase de la facturation mensuelle Lil & Co (super admin).
 * Résilient : renvoie ok/erreur, ne jette jamais.
 *
 *  - `super_commission_stats` (RPC, super_admin) : commandes & commissions par mois × resto ;
 *  - `invoice_email_log` (table, RLS super_admin) : statut d'envoi des factures ;
 *  - `odoo-send-invoices` (Edge Function, JWT super_admin) : génère, VALIDE et
 *    ENVOIE par email la facture du mois civil choisi (PDF Odoo joint).
 */

export interface LoadMonthlyStatsResult {
  ok: boolean;
  rows: MonthlyStatRaw[];
  error?: string;
}

export async function loadMonthlyStats(): Promise<LoadMonthlyStatsResult> {
  const { data, error } = await supabase.rpc('super_commission_stats');
  if (error) return { ok: false, rows: [], error: error.message };
  return { ok: true, rows: Array.isArray(data) ? (data as MonthlyStatRaw[]) : [] };
}

/** Ligne du journal d'envoi (1 par facture envoyée, réf `LILCO-<slug>-<YYYY-MM>`). */
export interface InvoiceLogEntry {
  ref: string;
  invoice_id: number | null;
  invoice_name: string | null;
  amount_total: number | null;
  currency: string | null;
  sent_to: string | null;
  sent_at: string;
  status: string;
}

/** Journal des envois, indexé par réf. Vide si la table est inaccessible. */
export async function loadInvoiceLog(): Promise<Map<string, InvoiceLogEntry>> {
  const { data, error } = await supabase
    .from('invoice_email_log')
    .select('ref,invoice_id,invoice_name,amount_total,currency,sent_to,sent_at,status')
    .order('sent_at', { ascending: false });
  const map = new Map<string, InvoiceLogEntry>();
  if (error || !Array.isArray(data)) {
    if (error) console.warn('[invoice_email_log] indisponible:', error.message);
    return map;
  }
  for (const row of data as InvoiceLogEntry[]) {
    if (row?.ref && !map.has(row.ref)) map.set(row.ref, row);
  }
  return map;
}

export interface SendInvoiceResult {
  ok: boolean;
  message: string;
  invoiceName?: string;
}

/**
 * Génère + valide + envoie la facture d'un resto pour un mois civil.
 * `testTo` (optionnel) force le destinataire (email de test, ex. le tien).
 */
export async function sendMonthlyInvoice(
  slug: string,
  year: number,
  month: number,
  testTo?: string,
): Promise<SendInvoiceResult> {
  const body: Record<string, unknown> = { tenant_slug: slug, year, month };
  if (testTo) body.test_to = testTo;
  const { data, error } = await supabase.functions.invoke('odoo-send-invoices', { body });
  if (error) return { ok: false, message: await extractFnError(error, 'Envoi impossible.') };

  const res = (data ?? {}) as { ok?: boolean; error?: string; results?: unknown[] };
  if (!res.ok) return { ok: false, message: res.error ?? 'Échec.' };
  const r = (Array.isArray(res.results) ? res.results[0] : null) as {
    ok?: boolean; skipped?: boolean; reason?: string; error?: string;
    invoice?: string; sent_to?: string; total?: number;
  } | null;
  if (!r) return { ok: false, message: 'Réponse vide.' };
  if (r.error) return { ok: false, message: r.error };
  if (r.skipped) return { ok: true, message: r.reason ?? 'Rien à envoyer ce mois.' };
  return {
    ok: true,
    invoiceName: r.invoice,
    message: `✅ ${r.invoice ?? 'Facture'} validée et envoyée à ${r.sent_to ?? '?'}.`,
  };
}

export interface AdminOpResult { ok: boolean; message: string }

async function callAdminOps(body: Record<string, unknown>, okMessage: string): Promise<AdminOpResult> {
  const { data, error } = await supabase.functions.invoke('odoo-admin-ops', { body });
  if (error) return { ok: false, message: await extractFnError(error, 'Opération impossible.') };
  const res = (data ?? {}) as { ok?: boolean; error?: string };
  if (!res.ok) return { ok: false, message: res.error ?? 'Échec.' };
  return { ok: true, message: okMessage };
}

/**
 * Repasse une facture VALIDÉE en brouillon dans Odoo → modifiable dans Odoo,
 * puis re-validation manuelle. Le journal d'envoi n'est pas touché.
 */
export function resetInvoiceToDraft(invoiceId: number): Promise<AdminOpResult> {
  return callAdminOps(
    { action: 'reset_to_draft', invoice_id: invoiceId },
    '↩️ Repassée en brouillon — modifie-la dans Odoo puis re-valide.',
  );
}

/**
 * ANNULE la facture dans Odoo et efface la ligne du journal d'envoi :
 * le mois redevient « À générer » (refacturation propre possible).
 */
export function cancelInvoice(invoiceId: number, ref: string): Promise<AdminOpResult> {
  return callAdminOps(
    { action: 'cancel_invoice', invoice_id: invoiceId, clear_log_ref: ref },
    '🗑 Facture annulée — le mois est de nouveau facturable.',
  );
}
